import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Building2,
  // MapPin,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Award,
  AlertTriangle,
  Activity,
  PieChart as PieChartIcon,
  BarChart3,
  X,
  Layers,
  TreePine,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/pmshribanner.webp';
import {
  useGetUtilizationEfficiencyQuery,
  useGetExpenseActivityTreeQuery,
  useGetExpenditureActivityTreeQuery,
  useGetMonthlyFundFlowTrendQuery,
  useGetYearlyFundFlowTrendQuery,
  useGetTopPerformingSchoolsQuery,
  useGetBottomPerformingSchoolsQuery,
  useGetTopRegionWisePerformanceQuery,
  useGetTotalReceivedAmountQuery,
} from '../services/api';
import { selectTotalSchools } from '../../../../store/slices/pmShriConstantsSlice';

import {
  HeadWiseDonutChart,
  ExpenditureBarChart,
  UtilizationGauge,
  TrendLineChart,
  // PerformanceHeatmap,
  SparklineCard,
  RankingTable,
  RegionPerformanceBarChart,
  HeadBreakdownCard,
} from '../components/charts';

import { YearDropdown, PhaseDropdown } from '../components/filters';

import type {
  // PMShriDashboardMetrics,
  // Head,
  SchoolPerformance,
  RegionPerformance,
  MonthlyTrendData,
  PMShriFilters,
  // PhaseWiseSummary,
  PMShriPhase,
} from '../types';
// import { DEFAULT_HEADS, HEAD_COLORS } from '../types';
// import { pmShriApi } from '../services/api';



type TabType = 'overview' | 'heads' | 'expenditure' | 'performance';

const PMShriDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const carouselImages = [img1];

  // Get total schools from Redux store
  const totalPMSHRISchools = useSelector(selectTotalSchools);

  // State
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_filters, setFilters] = useState<PMShriFilters>({});
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<PMShriPhase | null>(null);
  const [distributionMode, setDistributionMode] = useState<'sanctioned' | 'received' | 'spent'>('sanctioned');

  // Year and Phase filter state (null means "all")
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedPhaseFilter, setSelectedPhaseFilter] = useState<number | null>(null);
  const [trendViewYear, setTrendViewYear] = useState<number | null>(2025); // null means "All Years"

  // Get current year and phase from filter state
  const currentYear = selectedYear || 2025; // Default to 2025 if "all" (for APIs that require year)
  const currentPhase = selectedPhaseFilter || undefined;

  // RTK Query hooks for financial data
  // Primary data source: expense activity tree (most reliable)
  const { data: expenseTreeData, isLoading: expenseTreeLoading } = useGetExpenseActivityTreeQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    ...(currentPhase !== undefined && { phase: currentPhase })
  });

  // Expenditure tree for recurring/non-recurring breakdown
  const { data: expenditureTreeData } = useGetExpenditureActivityTreeQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    ...(currentPhase !== undefined && { phase: currentPhase })
  });

  // Total received amount from dedicated API
  const { data: totalReceivedData, isLoading: totalReceivedLoading } = useGetTotalReceivedAmountQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    ...(currentPhase !== undefined && { phase: currentPhase })
  });

  // Secondary APIs for additional data
  const { data: utilizationData, isLoading: utilizationLoading } = useGetUtilizationEfficiencyQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    ...(currentPhase !== undefined && { phase: currentPhase })
  });
  const { data: monthlyTrendDataRaw, isLoading: trendLoading } = useGetMonthlyFundFlowTrendQuery({
    ...(trendViewYear !== null && { year: trendViewYear }),
    ...(currentPhase !== undefined && { phase: currentPhase })
  });
  const { data: yearlyTrendDataRaw } = useGetYearlyFundFlowTrendQuery({
    ...(currentPhase !== undefined && { phase: currentPhase })
  });
  const { data: topSchoolsData, isLoading: topSchoolsLoading } = useGetTopPerformingSchoolsQuery({
    page: 1,
    ...(selectedYear !== null && { year: selectedYear }),
    ...(currentPhase !== undefined && { phase: currentPhase }),
    limit: 10
  });
  const { data: bottomSchoolsData, isLoading: bottomSchoolsLoading } = useGetBottomPerformingSchoolsQuery({
    page: 1,
    ...(selectedYear !== null && { year: selectedYear }),
    ...(currentPhase !== undefined && { phase: currentPhase }),
    limit: 10
  });
  const { data: regionPerformanceData, isLoading: regionPerformanceLoading } = useGetTopRegionWisePerformanceQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    ...(currentPhase !== undefined && { phase: currentPhase })
  });

  // Calculate totals from expense tree data (primary source) and dedicated API
  const { totalSanctioned, totalReceived, totalSpent, totalRecurring, totalNonRecurring } = useMemo(() => {
    if (!expenseTreeData?.success || !expenseTreeData?.data?.tree) {
      return {
        totalSanctioned: 0,
        totalReceived: 0,
        totalSpent: 0,
        totalRecurring: 0,
        totalNonRecurring: 0
      };
    }

    // Sum up all head-level totals from expense tree
    const sanctioned = expenseTreeData.data.tree.reduce((sum, head) => sum + (head.totalSanctioned || 0), 0);
    const spent = expenseTreeData.data.tree.reduce((sum, head) => sum + (head.totalSpent || 0), 0);
    const recurring = expenseTreeData.data.tree.reduce((sum, head) => sum + (head.totalRecurringAmount || 0), 0);
    const nonRecurring = expenseTreeData.data.tree.reduce((sum, head) => sum + (head.totalNonRecurringAmount || 0), 0);

    // Get total received from dedicated API endpoint (already in correct format, no conversion needed)
    const received = totalReceivedData?.success && totalReceivedData?.data?.totalAmountRecieved 
      ? totalReceivedData.data.totalAmountRecieved
      : 0;

    return {
      totalSanctioned: sanctioned,
      totalReceived: received,
      totalSpent: spent,
      totalRecurring: recurring,
      totalNonRecurring: nonRecurring
    };
  }, [expenseTreeData, totalReceivedData]);

  // Transform trend data based on view type
  const monthlyTrendData = useMemo(() => {
    // If "All Years" is selected, use yearly data
    if (trendViewYear === null && yearlyTrendDataRaw?.success) {
      const currentYear = new Date().getFullYear();
      return Object.entries(yearlyTrendDataRaw.data)
        .filter(([year]) => {
          const yearNum = parseInt(year);
          return yearNum >= 2023 && yearNum <= currentYear;
        })
        .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
        .map(([year, values]) => {
          const yearNum = parseInt(year);
          const utilization = parseFloat(values.totalUtilization.replace('%', ''));
          return {
            month: `${yearNum}-${(yearNum + 1).toString().slice(-2)}`, // e.g., "2024-25"
            year: yearNum,
            sanctioned: values.totalSanctioned || 0,
            received: values.totalReceived || 0,
            spent: values.totalSpent || 0,
            utilization: utilization || 0,
            recurringSpent: 0,
            nonRecurringSpent: 0
          };
        });
    }

    // Otherwise use monthly data
    if (!monthlyTrendDataRaw?.success || !monthlyTrendDataRaw?.data) {
      return [];
    }

    const monthOrder = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

    // Helper to extract month name from "Mon YYYY"
    // const getMonthName = (key: string) => key.split(' ')[0];

    const trends = Object.entries(monthlyTrendDataRaw.data as Record<string, any>).map(([key, value]) => {
      const [monthStr, yearStr] = key.split(' ');
      return {
        month: monthStr,
        year: parseInt(yearStr),
        sanctioned: value.totalSanctioned,
        received: value.totalReceived,
        spent: value.totalSpent,
        utilization: parseFloat((value.totalUtilization ?? '0').replace('%', '')),
        recurringSpent: 0, // Not provided in this API yet
        nonRecurringSpent: 0, // Not provided in this API yet
      } as MonthlyTrendData;
    });

    // Sort by financial year month order
    return trends.sort((a, b) => {
      // const idxA = monthOrder.indexOf(a.month);
      // const idxB = monthOrder.indexOf(b.month);
      // Adjust for year rollover if needed, but simple index sort works for standard FY view 
      // if we assume keys are correct. 
      // Better: Sort by date.
      const dateA = new Date(`${a.month} 1, ${a.year}`);
      const dateB = new Date(`${b.month} 1, ${b.year}`);
      return dateA.getTime() - dateB.getTime();
    });
  }, [trendViewYear, monthlyTrendDataRaw, yearlyTrendDataRaw]);

  // Extract recurring/non-recurring from expense tree (primary source)
  // Using totalRecurring and totalNonRecurring calculated from expense tree
  const recurringAmount = totalRecurring;
  const nonRecurringAmount = totalNonRecurring;

  // Calculate total recurring and non-recurring spent from expenditure tree
  const { recurringSpent, nonRecurringSpent } = useMemo(() => {
    if (!expenditureTreeData?.success || !expenditureTreeData?.data?.tree) {
      return { recurringSpent: 0, nonRecurringSpent: 0 };
    }

    const recSpent = expenditureTreeData.data.tree.reduce((sum, head) => sum + (head.spentRec || 0), 0);
    const nonRecSpent = expenditureTreeData.data.tree.reduce((sum, head) => sum + (head.spentNonRec || 0), 0);

    return {
      recurringSpent: recSpent,
      nonRecurringSpent: nonRecSpent
    };
  }, [expenditureTreeData]);

  // Extract utilization data
  const utilizationInfo = utilizationData?.success ? utilizationData.data.Utilization : null;
  const totalUnits = utilizationInfo?.['total Units'] || 0;
  const unitsCovered = utilizationInfo?.['Units Covered'] || 0;
  const unitsUncovered = utilizationInfo?.['Units unCovered'] || 0;

  // Calculate percentages from expense tree data
  const utilizationPercentage = totalReceived > 0 ? (totalSpent / totalReceived) * 100 : 0;
  const efficiencyPercentage = totalUnits > 0 ? (unitsCovered / totalUnits) * 100 : 0;

  // Transform top performing schools API data
  const topPerformingSchools = useMemo(() => {
    if (!topSchoolsData?.success || !topSchoolsData?.data?.data) return [];

    return topSchoolsData.data.data.map((school, index) => ({
      kvId: school.kvID,
      kvCode: `KV${String(school.kvID).padStart(4, '0')}`,
      schoolName: school.schoolName,
      kvName: school.schoolName,
      roId: 0, // Not provided in API
      roName: school.roName,
      region: school.roName,
      stateId: 0, // Not provided in API
      stateName: school.stateName,
      districtName: school.districtName,
      pmShriStatus: 'active' as const,
      pmShriPhase: school.phase as import('../types').PMShriPhase,
      totalSanctioned: school.sanctionedAmount, // API returns actual values
      totalReceived: school.receivedAmount, // API returns actual values
      totalSpent: school.spentAmount, // API returns actual values
      totalUnspent: school.receivedAmount - school.spentAmount,
      overallUtilization: typeof school.utilization === 'string' ? parseFloat(school.utilization.replace('%', '')) : school.utilization,
      utilizationPercentage: typeof school.utilization === 'string' ? parseFloat(school.utilization.replace('%', '')) : school.utilization,
      targetUnits: 0,
      completedUnits: 0,
      efficiencyPercentage: typeof school.utilization === 'string' ? parseFloat(school.utilization.replace('%', '')) : school.utilization,
      recurringAmount: 0,
      recurringSpent: 0,
      recurringUtilization: 0,
      nonRecurringAmount: 0,
      nonRecurringSpent: 0,
      nonRecurringUtilization: 0,
      headWiseData: [],
      performanceScore: typeof school.utilization === 'string' ? parseFloat(school.utilization.replace('%', '')) : school.utilization,
      rank: index + 1,
      trend: 'stable' as const,
    }));
  }, [topSchoolsData]);

  // Transform bottom performing schools API data
  const bottomPerformingSchools = useMemo(() => {
    if (!bottomSchoolsData?.success || !bottomSchoolsData?.data?.data) return [];

    return bottomSchoolsData.data.data.map((school, index) => ({
      kvId: school.kvID,
      kvCode: `KV${String(school.kvID).padStart(4, '0')}`,
      schoolName: school.schoolName,
      kvName: school.schoolName,
      roId: 0, // Not provided in API
      roName: school.roName,
      region: school.roName,
      stateId: 0, // Not provided in API
      stateName: school.stateName,
      districtName: school.districtName,
      pmShriStatus: 'active' as const,
      pmShriPhase: school.phase as import('../types').PMShriPhase,
      totalSanctioned: school.sanctionedAmount, // API returns actual values
      totalReceived: school.receivedAmount, // API returns actual values
      totalSpent: school.spentAmount, // API returns actual values
      totalUnspent: school.receivedAmount - school.spentAmount,
      overallUtilization: typeof school.utilization === 'string' ? parseFloat(school.utilization.replace('%', '')) : school.utilization,
      utilizationPercentage: typeof school.utilization === 'string' ? parseFloat(school.utilization.replace('%', '')) : school.utilization,
      targetUnits: 0,
      completedUnits: 0,
      efficiencyPercentage: typeof school.utilization === 'string' ? parseFloat(school.utilization.replace('%', '')) : school.utilization,
      recurringAmount: 0,
      recurringSpent: 0,
      recurringUtilization: 0,
      nonRecurringAmount: 0,
      nonRecurringSpent: 0,
      nonRecurringUtilization: 0,
      headWiseData: [],
      performanceScore: typeof school.utilization === 'string' ? parseFloat(school.utilization.replace('%', '')) : school.utilization,
      rank: index + 1,
      trend: 'stable' as const,
    }));
  }, [bottomSchoolsData]);

  // Transform region performance API data
  const regionPerformanceList = useMemo(() => {
    if (!regionPerformanceData?.success || !regionPerformanceData?.data?.data) return [];

    return regionPerformanceData.data.data.map((region, index) => ({
      roId: region.roid,
      roName: region.roName,
      roCode: `RO${String(region.roid).padStart(2, '0')}`,
      totalSchools: 0, // Not provided in API
      activeSchools: 0, // Not provided in API
      pmShriSchools: 0, // Not provided in API
      totalSanctioned: region.totalSanctionedAmount, // API returns actual values
      totalReceived: region.totalReceivedAmount, // API returns actual values
      totalSpent: region.totalSpentAmount, // API returns actual values
      totalUnspent: region.totalReceivedAmount - region.totalSpentAmount,
      overallUtilization: parseFloat(region.utilization),
      targetUnits: 0,
      completedUnits: 0,
      efficiencyPercentage: parseFloat(region.utilization),
      recurringAmount: region.recurringAmount, // API returns actual values
      recurringSpent: 0,
      recurringUtilization: 0,
      nonRecurringAmount: 0,
      nonRecurringSpent: 0,
      nonRecurringUtilization: 0,
      headWiseData: [],
      topPerformers: [],
      bottomPerformers: [],
      performanceScore: parseFloat(region.utilization),
      rank: index + 1,
      trend: 'stable' as const,
    }));
  }, [regionPerformanceData]);

  // Transform expense tree data to head-wise summary for chart
  const headWiseSummaryFromAPI = useMemo(() => {
    if (!expenseTreeData?.success || !expenseTreeData?.data?.tree) return [];

    const colorArray = ['#6B1786', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6'];
    const expenditureTree = expenditureTreeData?.data?.tree || [];

    return expenseTreeData.data.tree.map((head, index) => {
      const sanctioned = head.totalSanctioned || 0;
      const released = head.activeSanctionedAmount || 0;
      const spent = head.totalSpent || 0;

      // Get recurring/non-recurring from expenditure data
      const expenditureHead = expenditureTree.find(h => h.id === head.id);
      const spentRec = expenditureHead?.spentRec || 0;
      const spentNonRec = expenditureHead?.spentNonRec || 0;

      // Calculate utilization: (spent / sanctioned) * 100
      // Using sanctioned as the base for utilization calculation
      const utilization = sanctioned > 0 ? (spent / sanctioned) * 100 : 0;

      return {
        id: head.id,
        code: `H${head.id}` as import('../types').HeadCode,
        name: head.title,
        description: head.title,
        color: colorArray[index % colorArray.length],
        totalSanctioned: sanctioned,
        totalReceived: released,
        totalSpent: spent,
        totalUnspent: sanctioned - spent,
        utilizationPercentage: utilization,
        targetUnits: 0,
        completedUnits: 0,
        efficiencyPercentage: 0,
        subheadCount: head.children?.length || 0,
        recurringSpent: spentRec,
        nonRecurringSpent: spentNonRec,
      };
    });
  }, [expenseTreeData, expenditureTreeData]);

  // Format currency with no abbreviation - show full numbers
  const formatCurrency = (value: number | undefined) => {
    const num = value ?? 0;
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`;
  };

  // Load data - handled by RTK Query hooks
  const loadData = useCallback(() => {
    // Force refetch logic if needed, but for now RTK Query handles it.
    // Triggering a refresh could be done by invalidating tags or refetching.
    // Since we don't have direct refetch functions exposed for all, we can just reload window or rely on auto-refresh.
    // For this implementation, we'll assume the hook handles loading.
  }, []);

  // Removed useEffect for loadData as it's no longer needed for mock data.



  // Expenditure data for bar chart - use actual recurring/non-recurring from API
  const expenditureData = useMemo(() => {
    return headWiseSummaryFromAPI.map(head => ({
      name: head.code,
      recurring: head.recurringSpent || 0,
      nonRecurring: head.nonRecurringSpent || 0,
    }));
  }, [headWiseSummaryFromAPI]);

  // Render Overview Tab
  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <SparklineCard
          title="Total Sanctioned"
          value={formatCurrency(totalSanctioned)}
          subValue={""}
          data={monthlyTrendData.map(t => t.sanctioned || 0)}
          trend="up"
          trendValue={8.5}
          color="#6B1786"
          icon={<IndianRupee className="w-5 h-5" />}
        />
        <SparklineCard
          title="Total Released"
          value={formatCurrency(totalReceived)}
          subValue={`${((totalReceived / (totalSanctioned || 1)) * 100).toFixed(1)}% of Sanctioned`}
          data={monthlyTrendData.map(t => t.received || 0)}
          trend="up"
          trendValue={6.2}
          color="#059669"
          icon={<TrendingDown className="w-5 h-5" />}
        />
        <SparklineCard
          title="Total Spent"
          value={formatCurrency(totalSpent)}
          subValue={`${((totalSpent / (totalReceived || 1)) * 100).toFixed(1)}% of Received`}
          data={monthlyTrendData.map(t => t.spent || 0)}
          trend="up"
          trendValue={12.3}
          color="#10b981"
          icon={<TrendingUp className="w-5 h-5" />}
        />
        {/* <SparklineCard
          title="Overall Efficiency"
          value={`${(efficiencyPercentage || 0).toFixed(1)}%`}
          subValue={`${unitsCovered.toLocaleString()} / ${totalUnits.toLocaleString()} Units`}
          data={monthlyTrendData.map(t => t.utilization || 0)}
          trend={(efficiencyPercentage || 0) >= 80 ? 'up' : 'down'}
          trendValue={3.5}
          color={(efficiencyPercentage || 0) >= 80 ? '#10b981' : (efficiencyPercentage || 0) >= 60 ? '#f59e0b' : '#ef4444'}
          icon={<Award className="w-5 h-5" />}
        /> */}
        <SparklineCard
          title="Recurring / Non-Recurring"
          value={`Rec: ${formatCurrency(recurringAmount)}\nNon-Rec: ${formatCurrency(nonRecurringAmount)}`}
          subValue={''}
          data={monthlyTrendData.map(t => t.recurringSpent + t.nonRecurringSpent)}
          trend="up"
          trendValue={10.5}
          color="#3b82f6"
          icon={<Activity className="w-5 h-5" />}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Head-wise Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-2 min-h-[520px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Head-wise Distribution</h3>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setDistributionMode('sanctioned')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${distributionMode === 'sanctioned'
                  ? 'bg-white text-purple-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Sanctioned
              </button>
              <button
                onClick={() => setDistributionMode('received')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${distributionMode === 'received'
                  ? 'bg-white text-green-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Received
              </button>
              <button
                onClick={() => setDistributionMode('spent')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${distributionMode === 'spent'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Spent
              </button>
            </div>
          </div>
          <HeadWiseDonutChart
            data={headWiseSummaryFromAPI}
            height={380}
            valueType={distributionMode}
            centerLabel={distributionMode === 'sanctioned' ? 'Total Sanctioned' : distributionMode === 'received' ? 'Total Received' : 'Total Spent'}
            centerValue={
              distributionMode === 'sanctioned'
                ? formatCurrency(totalSanctioned)
                : distributionMode === 'received'
                  ? formatCurrency(totalReceived)
                  : formatCurrency(totalSpent)
            }
          />
        </div>

        {/* Utilization & Efficiency Overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilization & Efficiency</h3>
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center gap-8 w-full mb-4">
              <div className="text-center">
                <UtilizationGauge
                  value={utilizationPercentage}
                  size="md"
                  label="Utilization"
                />
                <div className="text-xs text-gray-500 mt-3">Spent / Received</div>
              </div>
              <div className="text-center">
                <UtilizationGauge
                  value={efficiencyPercentage}
                  size="md"
                  label="Efficiency"
                />
                <div className="text-xs text-gray-500 mt-3">Units Covered</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 w-full">
              <div className="text-center p-5 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-lg font-bold mt-2 -ml-3 text-blue-600">{formatCurrency(totalReceived)}</div>
                <div className="text-md text-gray-600 mt-2">Received</div>
              </div>
              <div className="text-center p-5 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-lg font-bold mt-2 -ml-3 text-purple-600">{formatCurrency(totalSpent)}</div>
                <div className="text-md text-gray-600 mt-2">Spent</div>
              </div>
              <div className="text-center p-5 bg-green-50 rounded-lg border border-green-200">
                <div className="text-lg font-bold mt-2 -ml-3 text-green-600">{utilizationPercentage.toFixed(1)}%</div>
                <div className="text-md text-gray-600 mt-2">Utilization</div>
              </div>
            </div>
          </div>
        </div>

        {/* Utilization Summary */}
        {/* <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Units Status</h3>
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="text-2xl font-bold text-gray-900">{totalUnits.toLocaleString()}</div>
                <div className="text-md text-gray-600">Total Units</div>
              </div>
              <Activity className="w-10 h-20 text-purple-600" />
            </div>
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <div className="text-2xl font-bold text-green-600">{unitsCovered.toLocaleString()}</div>
                <div className="text-md text-gray-600">Units Covered</div>
              </div>
              <Award className="w-10 h-20 text-green-600" />
            </div>
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
              <div>
                <div className="text-2xl font-bold text-yellow-600">{unitsUncovered.toLocaleString()}</div>
                <div className="text-md text-gray-600">Units Uncovered</div>
              </div>
              <AlertTriangle className="w-10 h-20 text-yellow-600" />
            </div>
          </div>
        </div> */}
      </div>

      {/* Trend Chart */}
      {/* <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {trendViewYear === null ? 'Yearly' : 'Monthly'} Fund Flow & Utilization Trend
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={trendViewYear === null ? 'all' : trendViewYear}
              onChange={(e) => setTrendViewYear(e.target.value === 'all' ? null : parseInt(e.target.value))}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white text-gray-700 font-medium"
            >
              <option value="all">All Years</option>
              <option value="2023">FY 2023-24</option>
              <option value="2024">FY 2024-25</option>
              <option value="2025">FY 2025-26</option>
              <option value="2026">FY 2026-27</option>
            </select>
          </div>
        </div>
        <TrendLineChart
          data={monthlyTrendData}
          height={350}
          showArea={true}
          dataKeys={['sanctioned', 'received', 'spent', 'utilization']}
        />
      </div> */}

      {/* Top/Bottom Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankingTable
          data={topPerformingSchools}
          type="school"
          title="🏆 Top Performing Schools"
          limit={10}
          highlightTop={3}
          onRowClick={(item) => navigate(`/pm-shri/schools/${(item as SchoolPerformance).kvId}`)}
        />
        <RankingTable
          data={bottomPerformingSchools}
          type="school"
          title="⚠️ Schools Needing Attention"
          limit={10}
          sortDirection="asc"
          highlightTop={0}
          highlightBottom={3}
          onRowClick={(item) => navigate(`/pm-shri/schools/${(item as SchoolPerformance).kvId}`)}
        />
      </div>
    </div>
  );

  // Render Heads Tab
  const renderHeadsTab = () => {
    // Use API data
    const headsData = headWiseSummaryFromAPI;

    return (
      <div className="space-y-6">
        {/* Head Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {headsData.map((head) => (
            <HeadBreakdownCard
              key={head.id}
              head={head}
              onClick={() => navigate(`/pm-shri/analytics/heads/${head.code}`)}
            />
          ))}
        </div>

        {/* Head-wise Comparison Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Head-wise Sanctioned vs Spent</h3>
          <div className="h-[400px]">
            <ExpenditureBarChart
              data={headsData.map(h => ({
                name: h.code,
                recurring: h.recurringSpent || 0,
                nonRecurring: h.nonRecurringSpent || 0,
              }))}
              height={380}
              horizontal={true}
            />
          </div>

          {/* Head Legend */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-3">Head Definitions:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {headsData.map((head) => (
                <div key={head.id} className="flex items-start gap-3 text-sm">
                  <span className="font-bold text-purple-700 min-w-fit">{head.code}:</span>
                  <span className="text-gray-600">{head.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Expenditure Tab
  const renderExpenditureTab = () => (
    <div className="space-y-6">
      {/* Expenditure Type Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recurring */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-blue-100">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Recurring Expenditure</h3>
              <p className="text-sm text-gray-500">Training, activities, events</p>
            </div>
          </div>
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-3xl font-bold text-blue-600">
                {formatCurrency(recurringSpent)}
              </div>
              <div className="text-sm text-gray-500">
                of {formatCurrency(recurringAmount)} sanctioned
              </div>
            </div>
            <UtilizationGauge
              value={recurringAmount > 0 ? (recurringSpent / recurringAmount) * 100 : 0}
              size="sm"
              showLabel={false}
            />
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full"
              style={{ width: `${recurringAmount > 0 ? (recurringSpent / recurringAmount) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Non-Recurring */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-green-100">
              <Building2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Non-Recurring Expenditure</h3>
              <p className="text-sm text-gray-500">Lab setup, smart class, equipment</p>
            </div>
          </div>
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-3xl font-bold text-green-600">
                {formatCurrency(nonRecurringSpent)}
              </div>
              <div className="text-sm text-gray-500">
                of {formatCurrency(nonRecurringAmount)} sanctioned
              </div>
            </div>
            <UtilizationGauge
              value={nonRecurringAmount > 0 ? (nonRecurringSpent / nonRecurringAmount) * 100 : 0}
              size="sm"
              showLabel={false}
            />
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full"
              style={{ width: `${nonRecurringAmount > 0 ? (nonRecurringSpent / nonRecurringAmount) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Expenditure by Head */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Expenditure Type by Head</h3>
        <ExpenditureBarChart
          data={expenditureData}
          height={400}
          showLegend={true}
        />

        {/* Head Legend */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm font-semibold text-gray-700 mb-3">Head Definitions:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {headWiseSummaryFromAPI.map((head) => (
              <div key={head.id} className="flex items-start gap-3 text-sm">
                <span className="font-bold text-purple-700 min-w-fit">{head.code}:</span>
                <span className="text-gray-600">{head.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trend Comparison */}
      {/* <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recurring vs Non-Recurring Trend</h3>
        <TrendLineChart
          data={monthlyTrendData}
          height={350}
          dataKeys={['recurringSpent', 'nonRecurringSpent']}
          showArea={false}
        />
      </div> */}
    </div>
  );

  // Render Performance Tab
  const renderPerformanceTab = () => (
    <div className="space-y-6">
      {/* Region Performance Bar Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Region-wise Performance</h3>
        <RegionPerformanceBarChart
          data={regionPerformanceList}
          height={450}
          onClick={(region) => navigate(`/pm-shri/analytics/regions/${region.roId}`, {
            state: { selectedYear, selectedPhase: selectedPhaseFilter }
          })}
        />
      </div>

      {/* Top/Bottom Schools */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankingTable
          data={topPerformingSchools}
          type="school"
          title="🏆 Top 10 Performing Schools"
          limit={10}
          highlightTop={3}
          onRowClick={(item) => navigate(`/pm-shri/schools/${(item as SchoolPerformance).kvId}`)}
        />
        <RankingTable
          data={bottomPerformingSchools}
          type="school"
          title="⚠️ Bottom 10 Schools"
          limit={10}
          highlightBottom={3}
          onRowClick={(item) => navigate(`/pm-shri/schools/${(item as SchoolPerformance).kvId}`)}
        />
      </div>

      {/* Region Rankings */}
      <RankingTable
        data={regionPerformanceList}
        type="region"
        title="All Regions Ranking"
        limit={25}
        highlightTop={3}
        highlightBottom={3}
        onRowClick={(item) => navigate(`/pm-shri/analytics/regions/${(item as RegionPerformance).roId}`)}
      />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] flex items-center gap-2">
              <Award className="w-7 h-7" />
              PM-SHRI Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Fund utilization and performance tracking for PM-SHRI Schools
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Financial Year Selector */}
            <YearDropdown
              value={selectedYear}
              onChange={setSelectedYear}
            />

            {/* Phase Selector */}
            <PhaseDropdown
              value={selectedPhaseFilter}
              onChange={setSelectedPhaseFilter}
            />

            {/* Refresh Button */}
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>

            {/* Phase-wise Schools Button */}
            <button
              onClick={() => setShowPhaseModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-200 text-purple-700 font-medium rounded-lg hover:bg-purple-50 transition-colors"
            >
              <Layers className="w-4 h-4" />
              Phase-wise
            </button>

            {/* Activity Tree Button */}
            <button
              onClick={() => navigate('/pm-shri/activity-tree')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-teal-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-teal-700 transition-colors shadow-md"
            >
              <TreePine className="w-4 h-4" />
              Activity Tree
            </button>

            {/* Expense Activity Tree Button */}
            <button
              onClick={() => navigate('/pm-shri/expense-activity-tree')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors shadow-md"
            >
              <IndianRupee className="w-4 h-4" />
              Expense Tree
            </button>

            {/* Expenditure Activity Tree Button */}
            <button
              onClick={() => navigate('/pm-shri/expenditure-activity-tree')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-700 transition-colors shadow-md"
            >
              <TrendingUp className="w-4 h-4" />
              Expenditure Tree
            </button>

            {/* Analytics Button */}
            {/* <button
              onClick={() => navigate('/pm-shri/analytics')}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </button> */}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: PieChartIcon },
              { id: 'heads', label: 'Pillar', icon: BarChart3 },
              { id: 'expenditure', label: 'Expenditure', icon: IndianRupee },
              { id: 'performance', label: 'Performance', icon: Award },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${activeTab === tab.id
                  ? 'text-purple-700 border-purple-600 bg-purple-50'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={loadData}
              className="ml-auto px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {(isLoading || utilizationLoading || expenseTreeLoading || totalReceivedLoading || topSchoolsLoading || bottomSchoolsLoading || regionPerformanceLoading) ? (
          <div className="flex flex-col items-center justify-center py-20">
            <KVSLoader size={80} />
            <span className="mt-4 text-gray-600">Loading dashboard data...</span>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'heads' && renderHeadsTab()}
            {activeTab === 'expenditure' && renderExpenditureTab()}
            {activeTab === 'performance' && renderPerformanceTab()}
          </>
        )}
      </main>

      {/* Phase-wise Schools Modal */}
      {showPhaseModal && (
        <div className="fixed inset-0 z-50 max-w-8xl overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setShowPhaseModal(false)}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-purple-700">
                <div className="flex items-center gap-3">
                  <Layers className="w-6 h-6 text-white" />
                  <h2 className="text-xl font-bold text-white">PM-SHRI Schools by Phase</h2>
                </div>
                <button
                  onClick={() => setShowPhaseModal(false)}
                  className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Phase Tabs */}
              <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
                <button
                  onClick={() => setSelectedPhase(null)}
                  className={`px-6 py-3 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${selectedPhase === null
                    ? 'text-purple-700 border-purple-600 bg-white'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                    }`}
                >
                  All Phases ({totalPMSHRISchools})
                </button>
                {/* Phase summary data not available in API currently */}

              </div>

              <div className="p-6 bg-gray-50">
                <div className="flex items-center justify-center p-8 text-gray-500 italic">
                  Phase-wise summary metrics coming soon
                </div>
              </div>

              {/* Schools List */}
              <div className="p-6 overflow-y-auto max-h-[40vh]">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  {selectedPhase ? `Phase ${selectedPhase} Schools` : 'All PM-SHRI Schools'}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">School</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Region</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Sanctioned</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Received</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Spent</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Utilization</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Efficiency</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Phase</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topPerformingSchools
                        ?.concat(bottomPerformingSchools || [])
                        ?.filter(school => selectedPhase === null || school.pmShriPhase === selectedPhase)
                        ?.slice(0, 20)
                        ?.map((school, idx) => (
                          <tr
                            key={school.kvId || idx}
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => {
                              setShowPhaseModal(false);
                              navigate(`/pm-shri/schools/${school.kvId}`);
                            }}
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{school.kvName}</div>
                              <div className="text-xs text-gray-500">{school.kvCode}</div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{school.region || '-'}</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              {formatCurrency(school.totalSanctioned)}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-green-600">
                              {formatCurrency(school.totalReceived || 0)}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-blue-600">
                              {formatCurrency(school.totalSpent)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${school.utilizationPercentage >= 80
                                ? 'bg-green-100 text-green-800'
                                : school.utilizationPercentage >= 60
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                                }`}>
                                {school.utilizationPercentage.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(school.efficiencyPercentage || 0) >= 80
                                ? 'bg-green-100 text-green-800'
                                : (school.efficiencyPercentage || 0) >= 60
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                                }`}>
                                {(school.efficiencyPercentage || 0).toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                Phase {school.pmShriPhase || '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  Showing schools with efficiency based on received funds and completed units
                </span>
                <button
                  onClick={() => setShowPhaseModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PMShriDashboard;
